import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";

// Actions
import {
  downloadMediaPdfReport,
  getMediaDetails,
  uploadFileItems,
} from "../../../actions/campaign-media/CampaignMediaActions";
import { closeDownloadMediaReportForm } from "../../../actions/media-report/MediaReportActions";

// Constants and Utils
import { selectFile } from "../../../common-utils/file-utils/FileUtils";
import { ImageFileTypes } from "../../../constants/GeneralConstants";

// Custom Hooks
import { useCampaignMediaPdfReportDownload } from "../../../hooks/ReportsDownloadHooks";

// Components
import Spinner from "../../../components/spinner/Spinner";
import MediaInfo from "../../../mavin/components/media-info/MediaInfo";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import MediaSitePerformance from "../../../mavin/components/media-site-performance/MediaSitePerformance";
import LocationSummary from "../../../mavin/components/location-summary/LocationSummary";
import HourlyTrafficDataSection from "../../../mavin/components/hourly-traffic-data/HourlyTrafficDataSection";
import MediaImageWithMap from "../../../mavin/components/media-image-with-map/MediaImageWithMap";
import MediaReportDownload from "../../../mavin/components/media-report-download/MediaReportDownload";
import DownloadReportsForm from "../../../components/download-reports-from/DownloadReportsForm";

// Css
import "./CampaignMediaViewPage.scss";

/**
 * Page
 */
function CampaignMediaViewPage() {
  const dispatch = useDispatch();
  const { campaignId, mediaId } = useParams();

  // State
  const [radius, setRadius] = useState(1000);

  // Selector
  const fileItems = useSelector((state) => state.campaignMedia.fileItems);

  const loading = useSelector(
    (state) => state.campaignMedia.mediaDetailsLoading
  );

  const fileItemsLoading = useSelector(
    (state) => state.campaignMedia.fileItemsLoading
  );

  const media = useSelector((state) => state.campaignMedia.media.mediaDetails);
  const { title = "" } = media || {};

  const roadSegmentTrafficInfo =
    useSelector((state) => state.campaignMedia.roadSegmentTrafficInfo) || {};

  const downloadingReportLoading = useSelector(
    (state) => state.campaignMedia.downloadingReport
  );

  // modal state
  const openDownloadMediaReportModal = useSelector(
    (state) => state.mediaReport.openDownloadMediaReportModal
  );

  //Dispatch
  useEffect(() => {
    dispatch(getMediaDetails(campaignId, mediaId));
  }, [dispatch, campaignId, mediaId]);

  // pdf report download effects
  useCampaignMediaPdfReportDownload(title);

  // upload function
  async function selectAndUpload() {
    const files = await selectFile(true, ImageFileTypes);
    dispatch(uploadFileItems(campaignId, mediaId, files));
  }

  //Checks for page loading
  if (loading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  //Checks for media
  if (!media) {
    return null;
  }

  const { latitude, longitude } = media || {};

  const pageActions = (
    <MediaReportDownload loading={downloadingReportLoading} />
  );

  function handleDownloadReportSubmit(isSentViaEmail, emailIds) {
    dispatch(
      downloadMediaPdfReport(
        campaignId,
        mediaId,
        radius,
        isSentViaEmail,
        emailIds
      )
    );
  }

  return (
    <div className="content-wrapper media-page" id="cont-media-details">
      {/* Page Header */}
      <PageHeader title="Media Report" actions={pageActions} shadow={true} />

      {/* Page content */}
      <div className="page-content">
        {/* Media info  */}
        <MediaInfo media={media} titleClassName={"h2"} />
        <hr className="divider"></hr>

        {/* Site performance */}
        <MediaSitePerformance
          media={media}
          title={"Site Performance"}
          className="py-3"
        />
        <hr className="divider"></hr>

        {/* Media image and map view*/}
        <MediaImageWithMap
          media={media}
          fileItems={fileItems}
          loading={loading || fileItemsLoading}
          onClick={selectAndUpload}
        />

        {/* Hourly Traffic chart and Table */}
        <HourlyTrafficDataSection
          media={media}
          roadSegmentTrafficInfo={roadSegmentTrafficInfo}
        />

        {/* Location summary */}
        {latitude && longitude && (
          <LocationSummary
            latitude={latitude}
            longitude={longitude}
            radius={radius}
            setRadius={setRadius}
            mediaTitle={title}
          />
        )}
      </div>

      {/* MODALS */}
      {openDownloadMediaReportModal && (
        <DownloadReportsForm
          handleSubmit={handleDownloadReportSubmit}
          handleClose={() => dispatch(closeDownloadMediaReportForm())}
        />
      )}
    </div>
  );
}

export default CampaignMediaViewPage;
